import { HttpContext, httpResource } from '@angular/common/http';
import { Service, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../../../core/http/http-context-tokens';
import { AuthService } from '../../../core/services/auth/auth.service';
import { formatarCpf } from '../../../shared/utils/documento.util';
import { MeusDadosResponse } from '../models/meus-dados.model';
import { UFS } from '../models/solicitacao-servico.model';
import type { PerfilCidadao } from './perfil-cidadao.service';

const RECURSO = `${environment.apiUrl}/sai/core/MeusDados`;

/** O erro fica local: a aba mostra a própria falha, sem o modal do interceptor. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/**
 * Sexo do registro → opção do select "Gênero".
 *
 * Decisão da usuária (30/09/2026): o SAI não guarda gênero, e o sexo do cadastro ocupa o
 * lugar dele. Qualquer outro valor deixa o select em "Selecione".
 */
const GENERO_POR_SEXO: Readonly<Record<string, string>> = { M: 'Masculino', F: 'Feminino' };

/**
 * O cadastro de quem está logado, lido de `GET api/sai/core/MeusDados`.
 *
 * A chamada só sai com sessão, e depende do usuário: cada login a refaz, e o logout devolve
 * o recurso ao estado ocioso — o dado da pessoa anterior não fica na memória.
 *
 * ⚠️ **Só a aba "Dados pessoais" lê daqui.** Ouvidoria, SIC e o card de conferência da
 * solicitação de serviço continuam no cadastro de exemplo do `PerfilCidadaoService`, por
 * decisão da usuária (30/09/2026), até uma change própria.
 */
@Service()
export class MeusDadosService {
  private readonly auth = inject(AuthService);

  private readonly usuario = toSignal(this.auth.currentUser, {
    initialValue: this.auth.currentUserValue,
  });

  private readonly dadosApi = httpResource<ResponseApi<MeusDadosResponse>>(() => {
    // `undefined` é "nada a buscar": sem esta guarda a chamada sairia sem token e voltaria 401.
    if (!this.usuario()?.token) return undefined;

    return { url: RECURSO, context: ERRO_LOCAL() };
  });

  /**
   * Distingue "sessão encerrada" de "falha": num 401 o `ErrorInterceptor` faz logout, e o
   * recurso volta a ocioso em vez de ficar em erro.
   */
  readonly logado = computed(() => !!this.usuario()?.token);

  readonly carregando = computed(() => this.dadosApi.isLoading());

  readonly falhou = computed(() => this.dadosApi.error() !== undefined);

  /**
   * O cadastro traduzido para o formulário, ou `null` sem sessão, carregando ou em falha.
   *
   * O `error()` é consultado ANTES do `value()` de propósito: em estado de erro o `value()`
   * **lança**.
   */
  readonly perfil = computed<PerfilCidadao | null>(() => {
    if (this.dadosApi.error() !== undefined) return null;

    const corpo = this.dadosApi.value()?.data;

    return corpo ? perfilDosMeusDados(corpo) : null;
  });

  /** Refaz a chamada — é o "Tentar novamente" do aviso de falha. */
  recarregar(): void {
    this.dadosApi.reload();
  }
}

/** O contrato da API convertido no formulário da aba. */
export function perfilDosMeusDados(resposta: MeusDadosResponse): PerfilCidadao {
  const uf = texto(resposta.desUfEst).toUpperCase();

  return {
    nomeCompleto: texto(resposta.desNomePes),
    genero: GENERO_POR_SEXO[texto(resposta.desSexo).toUpperCase()] ?? '',
    // `1990-05-10T00:00:00` → `1990-05-10`, o formato que o `<input type="date">` aceita.
    dataNascimento: texto(resposta.datNascimentoPfi).slice(0, 10),
    cpf: formatarCpf(resposta.desCpf),

    enderecoCompleto: texto(resposta.desLogradouro),
    numero: texto(resposta.desNumeroEnd),
    complemento: texto(resposta.desComplementoEnd),
    cep: formatarCep(resposta.desCepEnd),
    bairro: texto(resposta.desBairroEnd),
    estado: UFS.includes(uf) ? uf : '',
    cidade: texto(resposta.desNomeMun),

    email: texto(resposta.desEmail),
    telefoneCelular01: formatarTelefone(resposta.desTelefone),

    // Sem coluna no SAI (nome social, raça/cor), sem fonte segura (RG; os dois telefones que o
    // cadastro não distingue) ou que nunca sai da API (senha) — design, D6.
    nomeSocial: '',
    racaCor: '',
    rg: '',
    senha: '',
    telefoneResidencial: '',
    telefoneCelular02: '',
  };
}

function texto(valor: string | null | undefined): string {
  return (valor ?? '').trim();
}

/** 8 dígitos viram `00000-000`; outro formato aparece como veio. */
function formatarCep(valor: string | null): string {
  const digitos = texto(valor).replace(/\D/g, '');

  return digitos.length === 8 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : texto(valor);
}

/** 10 ou 11 dígitos viram `(00) 0000-0000` / `(00) 00000-0000`; outro formato, como veio. */
function formatarTelefone(valor: string | null): string {
  const digitos = texto(valor).replace(/\D/g, '');

  if (digitos.length === 11) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
  }

  if (digitos.length === 10) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
  }

  return texto(valor);
}
